import { TxParams } from '@velocity-exchange/sdk';
import {
	LAMPORTS_PER_SOL,
	Transaction,
	TransactionInstruction,
	VersionedTransaction,
} from '@solana/web3.js';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { ComputeUnits, PriorityFee } from 'src/@types/types';

/**
 * Solana's maximum transaction size (1280 - 40 - 8 = 1232 bytes)
 * 1280 is IPv6 minimum MTU, 40 bytes IPv6 header, 8 bytes fragment header
 */
const PACKET_DATA_SIZE = 1232;

/**
 * Each signature in a Solana transaction is 64 bytes
 */
const SIGNATURE_LENGTH_IN_BYTES = 64;

/**
 * Safe transaction size limit accounting for signatures and overhead.
 * With 2 signers (fee payer + user), we need 128 bytes for signatures plus ~10 bytes buffer.
 * Using 1090 bytes to leave room for 2 signatures (128 bytes) + overhead (~14 bytes).
 */
const SAFE_TX_MESSAGE_SIZE =
	PACKET_DATA_SIZE - 2 * SIGNATURE_LENGTH_IN_BYTES - 14;

const hasVersionedMessage = (
	tx: Transaction | VersionedTransaction
): tx is VersionedTransaction =>
	'message' in tx &&
	typeof (tx as VersionedTransaction).message?.serialize === 'function';

/**
 * Gets the serialized message size of a transaction (without signatures).
 * This is used to estimate if a transaction will fit within Solana's size limits.
 */
function getTransactionMessageSize(
	tx: Transaction | VersionedTransaction
): number {
	if (hasVersionedMessage(tx)) {
		return tx.message.serialize().length;
	}
	return tx.serializeMessage().length;
}

/** Solana's maximum serialized transaction size, in bytes. */
export const MAX_TX_BYTE_SIZE = PACKET_DATA_SIZE;

/**
 * Serialized wire size of a built transaction, or `Infinity` if it can't be
 * serialized at all — web3.js encodes the message into a fixed 1232-byte buffer
 * and throws `RangeError: encoding overruns Uint8Array` on overflow, which by
 * definition means the transaction is oversized.
 *
 * Never throws, so callers can size-check a transaction before paying for a
 * simulation round-trip.
 */
export function getSerializedTransactionSize(
	tx: Transaction | VersionedTransaction
): number {
	try {
		const messageSize = getTransactionMessageSize(tx);
		const numSignatures = hasVersionedMessage(tx)
			? tx.message.header.numRequiredSignatures
			: (tx.signatures.length ?? 1);

		// 1 byte of shortvec length prefix for the signature array
		return 1 + numSignatures * SIGNATURE_LENGTH_IN_BYTES + messageSize;
	} catch {
		return Infinity;
	}
}

/**
 * Checks if an error is a transaction size error (encoding overruns or tx too large).
 */
export function isTransactionSizeError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	const message = error.message.toLowerCase();
	return (
		message.includes('encoding overruns') ||
		message.includes('transaction too large') ||
		/max: encoded\/raw \d+\/\d+/i.test(message)
	);
}

interface InstructionWithMarketIndex {
	marketIndex: number;
	instructions: TransactionInstruction[];
}

interface BatchResult {
	batches: InstructionWithMarketIndex[][];
	batchSizes: number[];
}

/**
 * Batches instruction sets by measured transaction size.
 * Each batch's combined instructions should fit within a single transaction.
 *
 * @param instructionSets - Array of instruction sets, each with a marketIndex and instructions
 * @param buildTransaction - Function to build a transaction from instructions (used for size measurement)
 * @param safeSizeLimit - Maximum message size in bytes (default: SAFE_TX_MESSAGE_SIZE)
 * @returns Promise resolving to batches of instruction sets and their estimated sizes
 */
export async function batchInstructionsBySize(
	instructionSets: InstructionWithMarketIndex[],
	buildTransaction: (
		ixs: TransactionInstruction[]
	) => Promise<Transaction | VersionedTransaction>,
	safeSizeLimit: number = SAFE_TX_MESSAGE_SIZE
): Promise<BatchResult> {
	if (instructionSets.length === 0) {
		return { batches: [], batchSizes: [] };
	}

	// If only one instruction set, return it as a single batch
	if (instructionSets.length === 1) {
		const tx = await buildTransaction(instructionSets[0].instructions);
		const size = getTransactionMessageSize(tx);
		return { batches: [[instructionSets[0]]], batchSizes: [size] };
	}

	const batches: InstructionWithMarketIndex[][] = [];
	const batchSizes: number[] = [];
	let currentBatch: InstructionWithMarketIndex[] = [];

	for (const instructionSet of instructionSets) {
		// Try adding this instruction set to the current batch
		const testBatchIxs = [
			...currentBatch.flatMap((set) => set.instructions),
			...instructionSet.instructions,
		];

		try {
			const testTx = await buildTransaction(testBatchIxs);
			const testSize = getTransactionMessageSize(testTx);

			if (testSize <= safeSizeLimit) {
				// Fits in current batch
				currentBatch.push(instructionSet);
			} else if (currentBatch.length === 0) {
				// Single instruction set is too large on its own - add it anyway
				// (will fail at send time, but we'll handle that error separately)
				currentBatch.push(instructionSet);
				batches.push(currentBatch);
				batchSizes.push(testSize);
				currentBatch = [];
			} else {
				// Doesn't fit - start a new batch
				// First, record the size of the current batch before we close it
				const currentBatchTx = await buildTransaction(
					currentBatch.flatMap((set) => set.instructions)
				);
				const currentBatchSize = getTransactionMessageSize(currentBatchTx);
				batches.push(currentBatch);
				batchSizes.push(currentBatchSize);

				// Start new batch with this instruction set
				currentBatch = [instructionSet];
			}
		} catch {
			// If building the test batch fails, finalize current batch and start fresh
			if (currentBatch.length > 0) {
				try {
					const currentBatchTx = await buildTransaction(
						currentBatch.flatMap((set) => set.instructions)
					);
					const currentBatchSize = getTransactionMessageSize(currentBatchTx);
					batches.push(currentBatch);
					batchSizes.push(currentBatchSize);
				} catch {
					// If current batch build also fails, add each item as separate batch
					for (const item of currentBatch) {
						batches.push([item]);
						batchSizes.push(0); // Size unknown due to build failure
					}
				}
			}
			currentBatch = [instructionSet];
		}
	}

	// Don't forget the last batch
	if (currentBatch.length > 0) {
		try {
			const finalBatchTx = await buildTransaction(
				currentBatch.flatMap((set) => set.instructions)
			);
			const finalBatchSize = getTransactionMessageSize(finalBatchTx);
			batches.push(currentBatch);
			batchSizes.push(finalBatchSize);
		} catch {
			// If final batch build fails, add each item as separate batch
			for (const item of currentBatch) {
				batches.push([item]);
				batchSizes.push(0); // Size unknown due to build failure
			}
		}
	}

	return { batches, batchSizes };
}

export const getTxParams = (
	getPriorityFeeForComputeUnits: (computeUnits: ComputeUnits) => PriorityFee,
	computeUnits: ComputeUnits = 600_000 as ComputeUnits,
	priorityFeeToUse: PriorityFee = Env.minimumPriorityFee,
	useSimulatedComputeUnits = Env.useSimulatedComputeUnitBudget,
	simulatedComputeUnitBufferMultiplier = Env.computeUnitBufferMultiplier,
	lowerBoundCu?: number
): TxParams => {
	console.log(
		`Using compute unit price ${priorityFeeToUse} for tx with ${computeUnits} compute units. Estimated priority fee = ${
			(priorityFeeToUse * computeUnits) / 10 ** 6 / LAMPORTS_PER_SOL
		} SOL`
	);

	return {
		computeUnitsPrice: priorityFeeToUse,
		computeUnits: computeUnits,
		useSimulatedComputeUnits: useSimulatedComputeUnits,
		computeUnitsBufferMultiplier: simulatedComputeUnitBufferMultiplier,
		useSimulatedComputeUnitsForCUPriceCalculation:
			Env.useSimulatedComputeUnitBudgetForFees,
		getCUPriceFromComputeUnits: Env.useSimulatedComputeUnitBudgetForFees
			? (unitsToQuery) => {
					return getPriorityFeeForComputeUnits(unitsToQuery as ComputeUnits);
				}
			: undefined,
		lowerBoundCu,
	};
};
